/**
 * Composer tool catalog — the single source of truth for the deliverable tools a
 * user can invoke from the chat composer, used by both the "+" menu and the slash
 * (`/`) command menu.
 *
 * Each tool's `prompt` is a self-contained instruction that ends with "as an
 * artifact" (except Prototype), so the agent reliably produces a deliverable. When
 * the slash menu is used, the tool's `prompt` is sent followed by whatever context
 * the user typed — see `ChatTextArea.handleSubmit`. Prototype is routed through
 * `onGeneratePrototype` when that callback is provided (credit check + prototyper
 * mode), and always maps to `forced_artifact: 'prototype'`.
 */

import type { ForcedArtifact } from '@ideoz/core';

export type ComposerToolCategory = 'Strategy' | 'Experience' | 'Build';

export interface ComposerTool {
  /** Stable id (also the slash-menu keyboard/test key). */
  id: string;
  /** Menu label. */
  label: string;
  /** The user message sent to the agent (the deliverable instruction). */
  prompt: string;
  category: ComposerToolCategory;
  /** One-line hint shown in the slash menu. */
  description: string;
}

export const COMPOSER_TOOLS: ReadonlyArray<ComposerTool> = [
  // Strategy (Atlas analytical artifacts)
  {
    id: 'market-research',
    label: 'Market Research Analysis',
    prompt: 'Run a market research analysis and present it as an artifact',
    category: 'Strategy',
    description: 'Size the market and map trends, segments, and demand for your idea.',
  },
  {
    id: 'competitors',
    label: 'Competitors Assessment',
    prompt: 'Run a competitors assessment and present it as an artifact',
    category: 'Strategy',
    description: 'Compare direct and indirect competitors and find your wedge.',
  },
  {
    id: 'five-question-screen',
    label: '5-Question Internal Screen',
    prompt: 'Run the 5-question internal screen and present it as an artifact',
    category: 'Strategy',
    description: 'Quick internal gut-check on whether this idea is worth pursuing.',
  },
  {
    id: 'roi',
    label: 'Check ROI in detail',
    prompt: 'Check the ROI in detail and present it as an artifact',
    category: 'Strategy',
    description: 'Model the cost, return, and payback of building this.',
  },
  // Experience (product/UX artifacts)
  {
    id: 'jtbd',
    label: 'JTBD Problem statement',
    prompt: 'Generate a JTBD problem statement as an artifact',
    category: 'Experience',
    description: 'Frame the job your users are hiring the product to do.',
  },
  {
    id: 'persona',
    label: 'Persona',
    prompt: 'Generate a persona as an artifact',
    category: 'Experience',
    description: 'Build a grounded user persona: goals, pains, and context.',
  },
  {
    id: 'hmw',
    label: 'Ideation (HMW)',
    prompt: 'Generate How Might We questions as an artifact',
    category: 'Experience',
    description: 'Turn problems into "How Might We" prompts to spark solutions.',
  },
  {
    id: 'five-whys',
    label: '5 Whys',
    prompt: 'Run a 5 Whys analysis and present it as an artifact',
    category: 'Experience',
    description: 'Drill past symptoms to the root cause of a problem.',
  },
  {
    id: 'user-story',
    label: 'User Story',
    prompt: 'Generate a user story as an artifact',
    category: 'Experience',
    description: 'Capture a feature as "As a <role>, I want <goal>, so that <benefit>".',
  },
  // Build
  {
    id: 'prototype',
    label: 'Prototype',
    prompt: 'Generate a prototype',
    category: 'Build',
    description: 'Generate a working prototype of the screens you describe.',
  },
];

/** Category display order for the menus. */
export const COMPOSER_TOOL_CATEGORY_ORDER: ReadonlyArray<ComposerToolCategory> = [
  'Strategy',
  'Experience',
  'Build',
];

/**
 * Filter tools by a slash-menu query (matches the label, id, or description,
 * case-insensitively), preserving catalog order.
 */
export function filterComposerTools(query: string): ComposerTool[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...COMPOSER_TOOLS];
  return COMPOSER_TOOLS.filter(
    (t) =>
      t.label.toLowerCase().includes(q) ||
      t.id.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q)
  );
}

/**
 * Map a composer tool to its `forced_artifact` request value (the backend's snake_case
 * artifact type name), or `undefined` when the tool does not force an artifact.
 *
 * Nine Strategy / Experience tools map to their artifact type (including
 * `user-story` → `user_story`). `prototype` (Build) maps to `'prototype'`; when
 * `onGeneratePrototype` is provided, `ChatTextArea` still routes Prototype through
 * that callback (credit check + prototyper mode).
 * A `null`/no tool returns `undefined` (ordinary context-aware routing).
 */
const COMPOSER_TOOL_TO_FORCED_ARTIFACT: Readonly<Record<string, ForcedArtifact>> = {
  'market-research': 'market_research',
  competitors: 'competitors_assessment',
  'five-question-screen': 'five_question_screen',
  roi: 'check_roi',
  jtbd: 'problem_statement',
  persona: 'persona',
  hmw: 'how_might_we',
  'five-whys': 'five_whys',
  'user-story': 'user_story',
  prototype: 'prototype',
};

export function composerToolToForcedArtifact(
  tool: ComposerTool | null | undefined
): ForcedArtifact | undefined {
  if (!tool) return undefined;
  return COMPOSER_TOOL_TO_FORCED_ARTIFACT[tool.id];
}

/**
 * True when the tool forces a Strategy / Experience artifact (`forced_artifact` is
 * one of the nine backend enum values, including `user_story`). False for Prototype.
 */
export function isArtifactComposerTool(tool: ComposerTool): boolean {
  const forced = composerToolToForcedArtifact(tool);
  return forced != null && forced !== 'prototype';
}

/** Group a (possibly filtered) tool list into non-empty category sections, in order. */
export function groupComposerTools(
  tools: ReadonlyArray<ComposerTool>
): Array<{ category: ComposerToolCategory; tools: ComposerTool[] }> {
  return COMPOSER_TOOL_CATEGORY_ORDER.map((category) => ({
    category,
    tools: tools.filter((t) => t.category === category),
  })).filter((g) => g.tools.length > 0);
}
